import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Grupo, ISgrupo } from '../../types/grupo/Grupo';
import { Panel } from 'primereact/panel';
import { GrupoForm } from './GrupoForm';
import { useCreateGrupo1Mutation, useCreateGrupoMutation, useDeleteGrupoMutation, useGetGrupoQuery, useUpdateGrupoMutation } from './grupoApiSlice';
import errorFormat from '../../utils/errorFormat';
import { Link, useParams } from 'react-router-dom';
import { errorMonitor } from 'events';
import configUrls from '../../config/configUrls';
import axiosInstance from '../../app/axiosInstance';
import { AxiosError } from 'axios';
import { ErrorMessage } from '../../types/ErrorMessage';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import './dataTable.css';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmDialog from '../../components/ConfirmDialog';
import ConfirmDialogList from '../../components/ConfirmDialogList';

export const GrupoList = () => {
  const [search, setSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  //const [deleteGrupo, deleteGrupoStatus] = useDeleteGrupoMutation();
  const [deleteGrupo, { error, isLoading, isSuccess }] = useDeleteGrupoMutation();

  const [grupo, setGrupo] = useState<Grupo[]>([]);

  const [grupoToDelete, setGrupoToDelete] = useState<Grupo>(ISgrupo);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchDados();
    }, 700);
  }, [search]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/grupo/filter?search=${search}`);
      setGrupo(response.data);
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      enqueueSnackbar('Erro ao efetuar a consulta. Tente novamente mais tarde', { variant: 'error' });

      let erro: ErrorMessage | undefined;
      if (axiosError.response) {
        erro = axiosError.response.data as ErrorMessage;
      }
      if (erro && erro.message) {
        enqueueSnackbar(erro.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Mensagem de erro não disponível', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Apenas foca no input após a execução da pesquisa
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [grupo]);

  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };

  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteGrupo({ id: grupoToDelete.id ? grupoToDelete.id : '' });
  };
  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      fetchDados();
    }
    if (error) {
      enqueueSnackbar('Registro não deletado!', { variant: 'error' });
    }
  }, [isSuccess, error]);

  if (loading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  const header = (
    <>
      <div className="flex flex-wrap justify-content-end gap-2">
        <span>Grupo(s) encontrado(s): {grupo.length}</span>
      </div>
    </>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Editar">
          <Link to={`${configUrls.GRUPO_ACOMPANHAMENTO_EDIT_LK}${rowData.id}`}>
            <IconButton color="secondary" aria-label="edit">
              <EditIcon />
            </IconButton>
          </Link>
        </Tooltip>

        <Tooltip title="Excluir">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleExcluirtClick = (rowData: any) => {
    setGrupoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse Grupo?', grupoToDelete.nome ? grupoToDelete.nome : '']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />

      <Paper>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Lista de Grupos Acompanhamento
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button variant="contained" color="secondary" component={Link} to={configUrls.GRUPO_ACOMPANHAMENTO_CREATE}>
                    Novo
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChange} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>

          <Grid item xs={12} gap={2} mt={3}>
            <FormControl fullWidth>
              <DataTable value={grupo} paginator rows={15} showGridlines={true} size="small" columnResizeMode="expand" resizableColumns dataKey="id" header={header} className="custom-datatable">
                <Column field="id" header="ID" hidden sortable style={{ width: '25%' }}></Column>
                <Column field="nome" header="Nome" sortable style={{ width: '40%' }}></Column>
                <Column field="descricao" header="Descrição" sortable style={{ width: '60%' }}></Column>
                <Column field="acoes" header="Ações" body={renderAction} style={{ width: 'auto' }}></Column>
              </DataTable>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

/*
 <GrupoTurmaForm
                    grupoTurma={grupoTurmaState}
                    isdisabled={status.isLoading}
                    isLoading={status.isLoading}
                    handleSubmit={handleSubmit}
                    handleChange={handleChange}
                />
*/
